import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { PopoverModule } from 'primeng/popover';
import { AuthService } from '../../auth/auth.service';
import { ThemeService } from '../../shared/services/theme.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, PopoverModule],
  template: `
    <header class="header">
      <div class="header-left">
        <div class="search">
          <i class="pi pi-search"></i>
          <input type="text" placeholder="Search..." />
          <kbd>⌘K</kbd>
        </div>
      </div>

      <div class="header-right">
        <button
          class="icon-btn"
          [title]="themeService.isDark() ? 'Switch to light mode' : 'Switch to dark mode'"
          (click)="themeService.toggleTheme()"
        >
          <i
            class="pi"
            [class.pi-sun]="themeService.isDark()"
            [class.pi-moon]="!themeService.isDark()"
          ></i>
        </button>

        <button class="profile-btn" (click)="menu.toggle($event)">
          <div class="avatar">
            <span>A</span>
          </div>
        </button>

        <p-popover #menu>
          <div class="menu">
            <div class="menu-header">
              <div class="menu-avatar">A</div>
              <div>
                <div class="menu-name">Admin User</div>
                <div class="menu-email">admin&#64;cursive.ai</div>
              </div>
            </div>
            <div class="menu-divider"></div>
            <button class="menu-item" (click)="menu.hide()">
              <i class="pi pi-user"></i>
              <span>Profile Settings</span>
            </button>
            <button class="menu-item" (click)="menu.hide()">
              <i class="pi pi-cog"></i>
              <span>Preferences</span>
            </button>
            <div class="menu-divider"></div>
            <button class="menu-item destructive" (click)="logout(); menu.hide()">
              <i class="pi pi-sign-out"></i>
              <span>Sign out</span>
            </button>
          </div>
        </p-popover>
      </div>
    </header>
  `,
  styles: [
    `
      .header {
        height: var(--header-height);
        padding: 0 var(--space-6);
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: var(--color-bg);
        border-bottom: 1px solid var(--color-border);
      }

      .header-left {
        flex: 1;
        max-width: 400px;
      }

      .search {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-2) var(--space-4);
        background: var(--color-bg-subtle);
        border: 1px solid transparent;
        border-radius: var(--radius-md);
        transition: all var(--transition-fast);
        height: 36px;
      }

      .search:focus-within {
        border-color: var(--color-border-subtle);
        background: var(--color-bg-card);
      }

      .search i {
        color: var(--color-text-muted);
        font-size: var(--font-size-sm);
      }

      .search input {
        flex: 1;
        border: none;
        background: transparent;
        outline: none;
        font-family: var(--font-family);
        font-size: var(--font-size-sm);
        color: var(--color-text);
        min-width: 0;
      }

      .search input::placeholder {
        color: var(--color-text-muted);
      }

      .search kbd {
        padding: 2px 6px;
        background: var(--color-bg-muted);
        border-radius: var(--radius-sm);
        font-size: 10px;
        font-family: var(--font-family);
        font-weight: 500;
        color: var(--color-text-muted);
        flex-shrink: 0;
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      .icon-btn {
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        border-radius: var(--radius-md);
        color: var(--color-text-muted);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .icon-btn:hover {
        background: var(--color-bg-subtle);
        color: var(--color-text);
      }

      .icon-btn i {
        font-size: var(--font-size-base);
      }

      .profile-btn {
        display: flex;
        align-items: center;
        padding: var(--space-1);
        background: transparent;
        border: none;
        border-radius: var(--radius-md);
        cursor: pointer;
        transition: all var(--transition-fast);
        margin-left: var(--space-2);
      }

      .profile-btn:hover {
        background: var(--color-bg-subtle);
      }

      .avatar {
        width: 32px;
        height: 32px;
        background: var(--color-bg-muted);
        color: var(--color-text-secondary);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--font-size-xs);
        font-weight: 600;
      }

      /* Dropdown Menu */
      .menu {
        min-width: 220px;
        padding: var(--space-2);
        background: var(--color-bg-elevated);
      }

      .menu-header {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3);
      }

      .menu-avatar {
        width: 36px;
        height: 36px;
        background: var(--color-bg-muted);
        color: var(--color-text-secondary);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--font-size-sm);
        font-weight: 600;
        flex-shrink: 0;
      }

      .menu-name {
        font-weight: 600;
        font-size: var(--font-size-sm);
        color: var(--color-text);
      }

      .menu-email {
        font-size: var(--font-size-xs);
        color: var(--color-text-muted);
      }

      .menu-item {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        width: 100%;
        padding: var(--space-2) var(--space-3);
        background: transparent;
        border: none;
        border-radius: var(--radius-md);
        font-family: var(--font-family);
        font-size: var(--font-size-sm);
        font-weight: 500;
        color: var(--color-text);
        cursor: pointer;
        text-align: left;
        transition: all var(--transition-fast);
      }

      .menu-item:hover {
        background: var(--color-bg-subtle);
      }

      .menu-item i {
        font-size: var(--font-size-sm);
        color: var(--color-text-muted);
        width: 16px;
      }

      .menu-item.destructive {
        color: var(--color-danger);
      }

      .menu-item.destructive i {
        color: var(--color-danger);
      }

      .menu-item.destructive:hover {
        background: var(--color-danger-subtle);
      }

      .menu-divider {
        height: 1px;
        background: var(--color-border);
        margin: var(--space-2) 0;
      }

      @media (max-width: 640px) {
        .search {
          display: none;
        }
      }
    `,
  ],
})
export class Header {
  authService = inject(AuthService);
  themeService = inject(ThemeService);
  router = inject(Router);

  async logout() {
    await this.authService.logout();
  }
}
